<template>
  <v-dialog
    :model-value="modelValue"
    max-width="540"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-5">
      <page-title :value="title" />

      <p class="mt-3 text-body-2">
        対象クォーター: <strong>{{ monthsLabel }}</strong> 期 ({{ businessYear }}年度・第{{ quarter }}クォーター)
      </p>

      <v-alert
        variant="outlined"
        color="primary"
        icon="mdi-information-outline"
        class="mt-2"
        density="compact"
      >
        <ul class="ma-0 pl-4 text-body-2">
          <li>未作成の園児には新しくレポートを生成します。</li>
          <li>下書き状態の既存レポートは<strong>上書き</strong>されます。</li>
          <li>公開済のレポートは<strong>スキップ</strong>されます (上書きしません)。</li>
          <li>非同期処理のため完了まで数分かかる場合があります。</li>
        </ul>
      </v-alert>

      <div v-if="batch" class="mt-4">
        <p class="text-body-2 mb-1">
          生成中... {{ batch.successCount }} / {{ batch.totalCount }} 件
          <span v-if="batch.failedCount > 0" class="text-red">
            (失敗: {{ batch.failedCount }})
          </span>
        </p>
        <v-progress-linear
          :model-value="progressPercent"
          :indeterminate="batch.status === 'processing' && batch.successCount === 0"
          color="primary"
          height="14"
          rounded
        >
          <template v-slot:default>
            <span class="text-white text-caption">{{ progressPercent }}%</span>
          </template>
        </v-progress-linear>
        <p v-if="batch.status === 'completed'" class="mt-3 text-body-2 text-success">
          <v-icon size="small" color="success">mdi-check-circle</v-icon>
          生成が完了しました。
        </p>
        <p v-if="batch.status === 'failed'" class="mt-3 text-body-2 text-red">
          <v-icon size="small" color="red">mdi-alert-circle</v-icon>
          生成に失敗しました。
        </p>
      </div>

      <div class="d-flex mt-5" style="gap: 0.5rem;">
        <v-spacer />
        <v-btn variant="text" @click="close" :disabled="isStarting">
          {{ batch && batch.status === "completed" ? "閉じる" : "キャンセル" }}
        </v-btn>
        <v-btn
          v-if="!batch"
          color="primary"
          variant="flat"
          :loading="isStarting"
          @click="$emit('start')"
        >
          一括生成を開始
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import PageTitle from "@/components/atoms/PageTitle.vue";
import type { AiReportBatchResponse, AiReportQuarter } from "@/api/ApiAiReport";

export default defineComponent({
  name: "AiReportBulkGenerateDialog",
  components: { PageTitle },
  props: {
    modelValue: { type: Boolean, default: false },
    businessYear: { type: Number, required: true },
    quarter: { type: Number as PropType<AiReportQuarter>, required: true },
    monthsLabel: { type: String, required: true },
    batch: {
      type: Object as PropType<AiReportBatchResponse | null>,
      default: null,
    },
    isStarting: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "start", "close"],
  computed: {
    title(): string {
      return "VEVOレポートを一括生成";
    },
    progressPercent(): number {
      if (!this.batch || this.batch.totalCount === 0) return 0;
      return Math.floor((this.batch.successCount / this.batch.totalCount) * 100);
    },
  },
  methods: {
    close() {
      this.$emit("update:modelValue", false);
      this.$emit("close");
    },
  },
});
</script>
